import type { ObjectSnapshot, PresentationEvent } from '../net/protocol.js';
import type { Camera, Viewport } from './input.js';
import { renderMagicWand } from './magic-wand.js';
import { BALANCE_SPECIAL_GEOMETRY_V0 } from '../sim/basic-attack-geometry.js';
import { weaponSkinScale, WEAPON_SKIN_SPRITES_V1, type WeaponSkinSpriteKey } from '../reference/weapon-skin-sprites-v1.js';
import type { LoadedWeaponSkin, WeaponSkinLookup } from './weapon-skins.js';

type Player = Extract<ObjectSnapshot, { kind: 'player' }>;
type WeaponKind = 'SWEEP' | 'ASSASSIN' | 'DUAL' | 'THRUST' | 'SPIN' | 'BOW' | 'MAGIC';
export interface AttackPose {
  readonly x: number; readonly y: number; readonly angle: number; readonly reachM: number;
  readonly kind: WeaponKind; readonly progress: number; readonly contactProgress: number | undefined;
  /** Sword and greatsword take their visible arc from the authoritative preview geometry. */
  readonly sweepAngleDeg?: number;
  /** Selects the presentation skin; never inferred from kind or reach. */
  readonly weaponId?: string | null;
  /** Presentation-only: this swing turns the other way from the previous one. */
  readonly mirrored?: boolean;
}
interface AttackState {
  readonly tick: number;
  readonly sourceId: string;
  readonly attackId: string;
  readonly authoritative: boolean;
  readonly startedAt: number;
  readonly windupMs: number;
  readonly cycleMs: number;
  readonly weaponId: string | null;
  /** The commit direction until the strike, then the direction the strike left along. */
  forward: { readonly x: number; readonly y: number };
  readonly mirrored: boolean;
  contactProgress?: number;
  contactReachM?: number;
}
const BASIC_ID = 'weapon_basic_attack';
/** How long a weapon contact that lands at once (a thrust) is animated. Presentation only. */
const INSTANT_CONTACT_MS = 250;
/** The blade holds its final position this long after a crafted sweep's last contact. */
const CRAFTED_LINGER_MS = 80;

/** A weapon action the server released as a crafted skill: where, which way, how far and how wide, and for how long. */
interface CraftedSwing {
  readonly startedAt: number;
  readonly durationMs: number;
  readonly x: number; readonly y: number; readonly angle: number;
  readonly reachM: number; readonly angleDeg: number;
  readonly kind: WeaponKind;
  readonly weaponId: string | null;
  /** The server's last reported sweep position; a new report that does not continue it starts a new swing. */
  lastProgress: number;
}

function weaponKindOf(weaponId: string | null): WeaponKind {
  return weaponId === 'ASSASSIN_BLADE' ? 'ASSASSIN'
    : weaponId === 'SPEAR' ? 'THRUST'
    : weaponId === 'DAGGER_PAIR' ? 'DUAL'
    : weaponId === 'HEAVY_AXE' ? 'SPIN'
    : weaponId === 'BOW_REFERENCE' ? 'BOW'
    : weaponId === null || weaponId.includes('CATALYST') ? 'MAGIC' : 'SWEEP';
}
/** Greatsword crosses its hit range in a fixed time rather than one set by its long windup. */
const GREATSWORD_SLASH_MS = 600;
/** Share of the hit range a blade travels before entering it and after leaving it. */
const OUTSIDE_RANGE_MARGIN = 0.2;
/** Heavy weapons carry more momentum into and out of their hit range. */
const HEAVY_OUTSIDE_RANGE_MARGIN = 0.35;
/** HEAVY-family swings: wider approach/follow-through, alternating direction per attack. */
const HEAVY_SWING_WEAPONS: ReadonlySet<string> = new Set(['HEAVY_AXE', 'GREATSWORD']);
/** Blade speed outside the hit range, as a share of its in-range speed. */
const OUTSIDE_RANGE_SPEED = 0.5;
/** Where in the range crossing the strike lands when the windup leaves room for the approach. */
const STRIKE_RANGE_FRACTION = 0.25;

export interface MeleeSwingTimeline {
  readonly leadStartMs: number; readonly enterMs: number; readonly exitMs: number; readonly endMs: number;
  readonly rangeMs: number; readonly outsideMs: number; readonly margin: number;
}

/**
 * A melee swing approaches its hit range slowly, crosses it at one constant
 * speed and slows again after it. The crossing takes `rangeMs`; the approach
 * starts no earlier than the attack and the strike lands inside the crossing,
 * early in it, so the whole hit range is visibly swept at one speed.
 */
export function meleeSwingTimeline(windupMs: number, rangeMs: number, margin = OUTSIDE_RANGE_MARGIN): MeleeSwingTimeline {
  const outsideMs = rangeMs * margin / OUTSIDE_RANGE_SPEED;
  const enterMs = Math.min(windupMs, Math.max(outsideMs, windupMs - STRIKE_RANGE_FRACTION * rangeMs));
  const exitMs = enterMs + rangeMs;
  return { leadStartMs: Math.max(0, enterMs - outsideMs), enterMs, exitMs, endMs: exitMs + outsideMs, rangeMs, outsideMs, margin };
}

/** 0..1 across the hit range, below 0 while approaching it, above 1 after it; undefined when not drawn. */
export function meleeSwingProgress(timeline: MeleeSwingTimeline, elapsedMs: number): number | undefined {
  if (elapsedMs < timeline.leadStartMs || elapsedMs > timeline.endMs) return undefined;
  if (elapsedMs < timeline.enterMs) return -timeline.margin * (timeline.enterMs - elapsedMs) / timeline.outsideMs;
  if (elapsedMs <= timeline.exitMs) return (elapsedMs - timeline.enterMs) / timeline.rangeMs;
  return 1 + timeline.margin * (elapsedMs - timeline.exitMs) / timeline.outsideMs;
}
const clamp = (n: number): number => Math.max(0, Math.min(1, n));
const valid = (value: number): boolean => Number.isFinite(value) && value > 0;

/** Server events own identity/timing; interpolated player transforms own position. */
export class BasicAttackAnimations {
  private readonly active = new Map<string, AttackState>();
  private readonly lastTick = new Map<string, number>();
  private readonly mirrored = new Map<string, boolean>();
  /** Crafted weapon swings by source and release; drawn from the server's contact geometry, not the basic attack's. */
  private readonly crafted = new Map<string, CraftedSwing>();
  readonly tickRate: number;
  constructor(tickRate: number) {
    this.tickRate = tickRate;
    if (!valid(tickRate)) throw new Error('tickRate must be positive and finite');
  }
  clear(): void { this.active.clear(); this.lastTick.clear(); this.mirrored.clear(); this.crafted.clear(); }

  private start(
    player: Player | undefined, id: string, attackId: string, tick: number,
    windupTicks: number, cycleTicks: number, weaponId: string | null,
    forward: { readonly x: number; readonly y: number }, now: number, authoritative = true,
  ): void {
    if (!player || !player.basicAttack || player.basicAttack.phase === 'READY' || player.down || !player.connected) return;
    if (!Number.isFinite(tick) || !valid(cycleTicks) || !Number.isFinite(windupTicks)) return;
    const current = this.active.get(id);
    if (current?.attackId === attackId || tick <= (this.lastTick.get(id) ?? -Infinity)) return;
    const length = Math.hypot(forward.x, forward.y);
    if (!Number.isFinite(length) || length <= 1e-9) return;
    const cycleMs = Math.max(1_000 / this.tickRate, cycleTicks * 1_000 / this.tickRate);
    const remainingMs = Math.max(0, player.basicAttack.remainingCycleS * 1_000);
    const startedAt = now - Math.max(0, cycleMs - remainingMs);
    const mirrored = !(this.mirrored.get(id) ?? true);
    this.mirrored.set(id, mirrored);
    this.active.set(id, { tick, sourceId: id, attackId, authoritative, startedAt,
      windupMs: Math.max(0, windupTicks * 1_000 / this.tickRate), cycleMs,
      weaponId, forward: { x: forward.x / length, y: forward.y / length }, mirrored });
    this.lastTick.set(id, tick);
  }

  /** Welcome and reconnect restore a swing even after its start event has passed. */
  hydrate(objects: readonly ObjectSnapshot[], now: number): void {
    for (const object of objects) {
      if (object.kind !== 'player') continue;
      const id = String(object.netId);
      const basic = object.basicAttack;
      const presentation = basic?.presentation;
      if (!object.connected || object.down || !basic || basic.phase === 'READY') {
        this.active.delete(id);
        continue;
      }
      if (!presentation) continue;
      this.start(object, id, presentation.attackId, presentation.startedServerTick,
        presentation.windupTicks, presentation.cycleTicks, presentation.weaponId, presentation.forward, now);
    }
  }

  accept(events: readonly PresentationEvent[], objects: readonly ObjectSnapshot[], now: number): void {
    const players = new Map(objects.filter((object): object is Player => object.kind === 'player').map((player) => [player.netId, player]));
    for (const event of events) {
      if (event.type === 'basic_attack_start') {
        this.start(players.get(event.sourceId), String(event.sourceId), event.attackId,
          event.startedServerTick, event.windupTicks, event.cycleTicks, event.weaponId, event.forward, now);
      } else if (event.type === 'cast' && event.skillId === BASIC_ID) {
        const id = String(event.sourceId);
        if (event.tick <= (this.lastTick.get(id) ?? -Infinity)) continue;
        const player = players.get(event.sourceId);
        const basic = player?.basicAttack;
        if (!basic) continue;
        const vector = basic.swingForward ?? player?.facing ?? { x: 1, y: 0 };
        this.start(player, id, `${id}:${event.tick}`, event.tick,
          Math.ceil(basic.remainingPreHitS * this.tickRate),
          Math.ceil(basic.remainingCycleS * this.tickRate), player?.weaponId ?? null, vector, now, false);
      } else if (event.type === 'weapon_contact' && event.skillId === BASIC_ID) {
        const state = this.active.get(String(event.sourceId));
        if (!state || event.tick < state.tick) continue;
        if (event.progress !== undefined && Number.isFinite(event.progress)) state.contactProgress = clamp(event.progress);
        if (event.geometry?.reachM !== undefined && valid(event.geometry.reachM)) state.contactReachM = event.geometry.reachM;
      } else if (event.type === 'weapon_contact') {
        this.startCrafted(event, players.get(event.sourceId), now);
      } else if (event.type === 'basic_attack_strike') {
        const state = this.active.get(String(event.sourceId));
        const length = Math.hypot(event.forward.x, event.forward.y);
        if (state?.attackId === event.attackId && Number.isFinite(length) && length > 1e-9) {
          state.forward = { x: event.forward.x / length, y: event.forward.y / length };
        }
      } else if (event.type === 'basic_attack_cancel') {
        const id = String(event.sourceId);
        if (this.active.get(id)?.attackId === event.attackId) this.active.delete(id);
      } else if (event.type === 'death') {
        this.active.delete(String(event.targetId));
        for (const key of this.crafted.keys()) if (key.startsWith(`${event.targetId}:`)) this.crafted.delete(key);
      }
    }
  }

  /**
   * A crafted weapon skill plays the held weapon's swing at the contact
   * geometry the server resolved for it: its reach and opening, along the
   * release's own heading. A sweep reports its progress every tick, so one
   * swing runs for the server's `durationS`; a contact that lands at once is
   * a short thrust. The release is told apart by the server's `attackId`.
   */
  private startCrafted(event: Extract<PresentationEvent, { type: 'weapon_contact' }>, player: Player | undefined, now: number): void {
    const reachM = event.geometry?.reachM; const angleDeg = event.geometry?.angleDeg;
    if (reachM === undefined || angleDeg === undefined || !valid(reachM) || !valid(angleDeg)) return;
    const key = `${event.sourceId}:${event.attackId ?? 0}`;
    const progress = event.progress ?? 0;
    const sweeping = event.durationS !== undefined && valid(event.durationS) && event.progress !== undefined;
    const current = this.crafted.get(key);
    if (current !== undefined && sweeping && progress > current.lastProgress) { current.lastProgress = progress; return; }
    const durationMs = sweeping ? event.durationS! * 1_000 : INSTANT_CONTACT_MS;
    const weaponId = player?.weaponId ?? null;
    this.crafted.set(key, {
      startedAt: now - (sweeping ? progress * durationMs : 0), durationMs,
      x: event.origin.x, y: event.origin.y, angle: Math.atan2(event.forward.y, event.forward.x),
      reachM, angleDeg, kind: event.geometry?.thrust === true ? 'THRUST' : weaponId === null ? 'SWEEP' : weaponKindOf(weaponId), weaponId, lastProgress: progress,
    });
  }

  poses(
    objects: readonly ObjectSnapshot[],
    geometry: (weaponId: string) => { readonly kind: string; readonly reachM?: number; readonly lengthM?: number; readonly angleDeg?: number } | undefined,
    now: number,
  ): AttackPose[] {
    const players = new Map(objects.filter((object): object is Player => object.kind === 'player').map((player) => [String(player.netId), player]));
    const poses: AttackPose[] = [];
    for (const [id, state] of this.active) {
      const player = players.get(id);
      const elapsed = Math.max(0, now - state.startedAt);
      if (!player || !player.connected || player.down || !player.basicAttack || player.basicAttack.phase === 'READY' || elapsed >= state.cycleMs) {
        this.active.delete(id);
        continue;
      }
      const type = weaponKindOf(state.weaponId);
      const range = state.weaponId ? geometry(state.weaponId) : undefined;
      const reachM = state.contactReachM ?? (range?.kind === 'sector' ? range.reachM : range?.kind === 'corridor' ? range.lengthM : undefined) ?? 0.9;
      if (!valid(reachM)) continue;
      if (type === 'BOW' || type === 'MAGIC') {
        const progress = state.authoritative
          ? clamp(elapsed <= state.windupMs
            ? 0.5 * elapsed / Math.max(1, state.windupMs)
            : 0.5 + 0.5 * (elapsed - state.windupMs) / Math.max(1, state.cycleMs - state.windupMs))
          : clamp(elapsed / state.cycleMs);
        poses.push({ x: player.x, y: player.y, angle: Math.atan2(state.forward.y, state.forward.x),
          reachM, kind: type, weaponId: state.weaponId, progress, contactProgress: state.contactProgress });
        continue;
      }
      // In-range speed is the one the swing already had: the windup covers half
      // the hit range (the axe keeps its former full-turn angular speed across
      // its sector; the greatsword keeps its fixed slash time).
      const sectorDeg = range?.kind === 'sector' && range.angleDeg !== undefined && valid(range.angleDeg) ? range.angleDeg : undefined;
      const windupMs = Math.max(1, state.windupMs);
      const rangeMs = state.weaponId === 'GREATSWORD' ? GREATSWORD_SLASH_MS
        : type === 'SPIN' ? 2 * windupMs * (sectorDeg ?? 360) / 360
        : 2 * windupMs;
      const heavy = state.weaponId !== null && HEAVY_SWING_WEAPONS.has(state.weaponId);
      const progress = meleeSwingProgress(meleeSwingTimeline(state.windupMs, rangeMs,
        heavy ? HEAVY_OUTSIDE_RANGE_MARGIN : OUTSIDE_RANGE_MARGIN), elapsed);
      if (progress === undefined) continue;
      poses.push({ x: player.x, y: player.y, angle: Math.atan2(state.forward.y, state.forward.x),
        reachM, kind: type, weaponId: state.weaponId, progress,
        contactProgress: state.weaponId === 'GREATSWORD' ? undefined : state.contactProgress,
        ...(heavy ? { mirrored: state.mirrored } : {}),
        ...(sectorDeg === undefined ? {} : { sweepAngleDeg: sectorDeg }) });
    }
    for (const [key, swing] of this.crafted) {
      const elapsed = Math.max(0, now - swing.startedAt);
      if (elapsed > swing.durationMs + CRAFTED_LINGER_MS) { this.crafted.delete(key); continue; }
      poses.push({ x: swing.x, y: swing.y, angle: swing.angle, reachM: swing.reachM, kind: swing.kind,
        progress: clamp(elapsed / swing.durationMs), contactProgress: undefined, weaponId: swing.weaponId,
        sweepAngleDeg: swing.angleDeg });
    }
    return poses;
  }
}

/** Minimal drawing surface: tests import this module without DOM globals. */
interface AttackCanvasContext {
  save(): void;
  restore(): void;
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void;
  translate(x: number, y: number): void;
  rotate(angle: number): void;
  strokeStyle: unknown;
  lineWidth: number;
  lineCap: string;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  stroke(): void;
  /** Optional so line-art-only surfaces keep working; skins are skipped without it. */
  drawImage?(image: unknown, dx: number, dy: number, dw: number, dh: number): void;
}

/** Draws a loaded sprite with its pivot at the current origin, long axis on +X. */
function drawWeaponSkin(ctx: AttackCanvasContext, skin: LoadedWeaponSkin, scale: number): void {
  const { sprite } = skin;
  ctx.save();
  ctx.rotate(sprite.artRotationRad);
  ctx.drawImage?.(skin.image, -sprite.pivotPx[0] * scale, -sprite.pivotPx[1] * scale,
    sprite.canvasPx[0] * scale, sprite.canvasPx[1] * scale);
  ctx.restore();
}

/** Dagger geometry renders two independent 140-degree strokes with a 40-degree
 * crossed center. Trails are presentation-only and never create hits. */
export function dualDaggerAnglesRad(progress: number): readonly [number, number] {
  return [(20 - 140 * progress) * Math.PI / 180, (-20 + 140 * progress) * Math.PI / 180];
}
/** Left/right hand mounts shared by the line art, its trails and the dagger sprites. */
const DAGGER_HAND_LATERAL_M = [
  WEAPON_SKIN_SPRITES_V1.DAGGER_PAIR_LEFT.mountLateralM, WEAPON_SKIN_SPRITES_V1.DAGGER_PAIR_RIGHT.mountLateralM,
] as const;
function renderDualDaggers(
  ctx: AttackCanvasContext, reachPx: number, pixels: number, progress: number,
  skins?: readonly [LoadedWeaponSkin, LoadedWeaponSkin],
): void {
  const draw = (angles: readonly [number, number], trail: boolean): void => {
    if (!trail && skins) {
      for (let hand = 0; hand < 2; hand++) {
        const skin = skins[hand]!;
        ctx.save();
        ctx.translate(0, DAGGER_HAND_LATERAL_M[hand]! * pixels);
        ctx.rotate(angles[hand]!);
        drawWeaponSkin(ctx, skin, weaponSkinScale(skin.sprite, pixels, reachPx / pixels));
        ctx.restore();
      }
      return;
    }
    ctx.lineWidth = Math.max(2, pixels * (trail ? 0.065 : 0.095));
    ctx.strokeStyle = trail ? 'rgba(255,232,163,0.28)' : '#ffe8a3';
    for (let hand = 0; hand < 2; hand++) {
      const angle = angles[hand]!;
      const originY = DAGGER_HAND_LATERAL_M[hand]! * pixels;
      ctx.beginPath();
      ctx.moveTo(pixels * 0.20, originY);
      ctx.lineTo(reachPx * Math.cos(angle), reachPx * Math.sin(angle) + originY);
      ctx.stroke();
    }
  };
  draw(dualDaggerAnglesRad(Math.max(0, progress - 0.10)), true);
  draw(dualDaggerAnglesRad(progress), false);
}

/** Draw the blade along the same [-sector/2, +sector/2] progression as the
 * authoritative angular contact resolver. Trails do not create hits. */
function renderAssassinBlade(
  ctx: AttackCanvasContext, reachPx: number, pixels: number, progress: number, skin?: LoadedWeaponSkin,
): void {
  const sectorDeg = BALANCE_SPECIAL_GEOMETRY_V0.ASSASSIN_BLADE.sectorDeg;
  const draw = (position: number, trail: boolean): void => {
    ctx.save();
    ctx.rotate((-sectorDeg / 2 + sectorDeg * position) * Math.PI / 180);
    if (!trail && skin) {
      drawWeaponSkin(ctx, skin, weaponSkinScale(skin.sprite, pixels, reachPx / pixels));
      ctx.restore();
      return;
    }
    ctx.strokeStyle = trail ? 'rgba(255,232,163,0.25)' : '#ffe8a3';
    ctx.lineWidth = Math.max(2, pixels * (trail ? 0.09 : 0.14));
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(pixels * 0.2, 0);
    ctx.lineTo(reachPx, 0);
    ctx.stroke();
    ctx.restore();
  };
  for (let i = 3; i >= 1; i -= 1) if (progress > i * 0.08) draw(progress - i * 0.08, true);
  draw(progress, false);
}

/** Canvas rendering cannot affect collision, attack power or hit registration. */
export function renderAttackPoses(
  ctx: AttackCanvasContext,
  poses: readonly AttackPose[],
  camera: Camera,
  viewport: Viewport,
  skins?: WeaponSkinLookup,
): void {
  const pixels = camera.pixelsPerMetre;
  const skin = (key: WeaponSkinSpriteKey): LoadedWeaponSkin | undefined => ctx.drawImage ? skins?.(key) : undefined;
  ctx.save();
  ctx.setTransform(viewport.devicePixelRatio, 0, 0, viewport.devicePixelRatio, 0, 0);
  for (const pose of poses) {
    const p = { x: (pose.x - camera.x) * pixels + viewport.cssWidth / 2, y: (pose.y - camera.y) * pixels + viewport.cssHeight / 2 };
    ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(pose.angle);
    const phase = pose.contactProgress ?? pose.progress;
    const key = pose.weaponId && pose.weaponId in WEAPON_SKIN_SPRITES_V1 ? pose.weaponId as WeaponSkinSpriteKey : undefined;
    const weaponSkin = key ? skin(key) : undefined;
    if (pose.kind === 'ASSASSIN') {
      renderAssassinBlade(ctx, pose.reachM * pixels, pixels, pose.progress, weaponSkin);
      ctx.restore();
      continue;
    }
    if (pose.kind === 'DUAL') {
      const left = pose.weaponId === 'DAGGER_PAIR' ? skin('DAGGER_PAIR_LEFT') : undefined;
      const right = pose.weaponId === 'DAGGER_PAIR' ? skin('DAGGER_PAIR_RIGHT') : undefined;
      renderDualDaggers(ctx, pose.reachM * pixels, pixels, phase, left && right ? [left, right] : undefined);
      ctx.restore();
      continue;
    }
    if (pose.kind === 'MAGIC') {
      renderMagicWand(ctx, pixels, pose.progress, weaponSkin);
      ctx.restore();
      continue;
    }
    const thrust = pose.kind === 'THRUST';
    const ranged = pose.kind === 'BOW';
    const sweepAngleRad = pose.sweepAngleDeg === undefined ? 1.7 : pose.sweepAngleDeg * Math.PI / 180;
    const angle = thrust || ranged ? 0 : (phase - 0.5) * sweepAngleRad * (pose.mirrored ? -1 : 1);
    ctx.rotate(angle);
    // Outside the hit range the spear eases back from its rest length.
    const extension = !thrust ? 1 : phase < 0 ? 0.4 + 0.5 * phase : phase > 1 ? 0.4 - 0.5 * (phase - 1) : 0.4 + 0.6 * Math.sin(Math.PI * phase);
    const length = pose.reachM * pixels * extension;
    if (weaponSkin && ranged) {
      const mount = weaponSkin.sprite.mountForwardM * pixels;
      ctx.translate(mount, 0);
      drawWeaponSkin(ctx, weaponSkin, weaponSkinScale(weaponSkin.sprite, pixels));
      ctx.strokeStyle = '#ffe8a3';
      ctx.lineWidth = Math.max(2, pixels * 0.085);
      ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(pixels * 0.22 * Math.sin(Math.PI * phase), 0); ctx.stroke();
      ctx.restore();
      continue;
    }
    if (weaponSkin) {
      // A thrust slides the full-length sprite so its tip follows the line art's extension.
      ctx.translate(length - pose.reachM * pixels, 0);
      drawWeaponSkin(ctx, weaponSkin, weaponSkinScale(weaponSkin.sprite, pixels, pose.reachM));
      ctx.restore();
      continue;
    }
    ctx.strokeStyle = '#ffe8a3';
    ctx.lineWidth = Math.max(2, pixels * (pose.kind === 'SPIN' ? 0.13 : 0.085));
    ctx.lineCap = 'round';
    ctx.beginPath();
    if (ranged) {
      const offset = Math.min(length, pixels * 0.45);
      ctx.moveTo(offset, -pixels * 0.27); ctx.lineTo(offset, pixels * 0.27);
      ctx.moveTo(offset, 0); ctx.lineTo(offset + pixels * 0.22 * Math.sin(Math.PI * phase), 0);
    } else {
      ctx.moveTo(pixels * 0.24, 0); ctx.lineTo(length, 0);
    }
    ctx.stroke(); ctx.restore();
  }
  ctx.restore();
}
